import { useState } from 'react'
import { mensagemDoErro } from '../../api/erros'
import { abrirDocumento } from '../../api/queries/documentos'
import { IconePagina } from '../../components/IconesDeDocumento'
import { TEXTOS } from '../../lib/glossario'
import type { CitacaoDoDocumento as Citacao } from '../../types/documentos'

/**
 * Uma fonte da resposta: o documento, a página, e as palavras dele entre aspas.
 *
 * **Duas vozes, duas letras.** O que o sistema diz está na letra da interface; o que o DOCUMENTO
 * diz está em serifa, como no papel do processo. Quem lê distingue sem esforço a explicação da
 * prova — e é a prova que vale. As aspas só aparecem quando o servidor conferiu que as palavras
 * estão no trecho; sem elas, fica o documento e a página para abrir.
 *
 * A etiqueta da página é o botão: um clique abre o PDF já na página citada.
 *
 * @param props a citação e a posição dela na lista
 * @returns a ficha da fonte
 */
export function CitacaoDoDocumento({ citacao, numero }: { citacao: Citacao; numero: number }) {
  const t = TEXTOS.consulta
  const [erro, setErro] = useState<string | null>(null)

  async function abrir() {
    setErro(null)
    try {
      await abrirDocumento(citacao.documentoId, citacao.pagina)
    } catch (falha) {
      setErro(mensagemDoErro(falha))
    }
  }

  return (
    <li className="relative overflow-hidden rounded-lg bg-stone-50 ring-1 ring-inset ring-stone-200">
      <div className="flex items-start justify-between gap-3 py-2.5 pl-4 pr-0">
        <p className="min-w-0 pt-0.5 text-xs text-slate-600">
          <span className="mr-1.5 font-semibold tabular-nums text-slate-400">{numero}</span>
          <span className="font-medium text-slate-800">{citacao.documento}</span>
          {/* O tipo só aparece quando o nome não o diz: "Termo de Referência · Termo de Referência" é ruído. */}
          {!citacao.documento.toLowerCase().includes(citacao.tipoRotulo.toLowerCase()) && (
            <span> · {citacao.tipoRotulo}</span>
          )}
        </p>
        <button
          type="button"
          onClick={() => void abrir()}
          title={t.abrirNaPagina(citacao.pagina)}
          className="flex shrink-0 items-center gap-1.5 rounded-l-md bg-niteroi-700 py-1 pl-2.5 pr-3 text-xs font-semibold text-white transition-colors hover:bg-niteroi-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-niteroi-600"
        >
          <IconePagina className="h-3.5 w-3.5" />
          <span className="tabular-nums">{t.pagina(citacao.pagina)}</span>
          <span className="sr-only">— {t.abrirNaPagina(citacao.pagina)}</span>
        </button>
      </div>
      {citacao.literal && (
        <blockquote className="border-t border-stone-200 px-4 pb-3 pt-2 font-serif text-[0.95rem] italic leading-relaxed text-stone-800">
          “{citacao.literal}”
        </blockquote>
      )}
      {erro && (
        <p role="alert" className="px-4 pb-2 text-xs text-red-800">
          {erro}
        </p>
      )}
    </li>
  )
}
